import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertDialog, Button, Callout, Card, EmptyState, Modal, SelectField, Spinner, Table, Tag, TextField } from '@humanforce/mango-ui-react';
import { ToastContainer, useToast } from '../../../components/ui';
import type { SaveWorkplaceRequestDto, WorkplaceDetailsDto, WorkplaceListDetailsDto, WorkplaceState } from '../organisationApiTypes';
import { closeWorkplace, createWorkplace, getWorkplaces, updateWorkplace, WorkplaceCloseBlockedError } from '../workplacesApi';

interface Props {
  businessRecordId: string;
}

// AC-019: the state picker offers exactly these eight values, no free text —
// the same array the database CHECK constraint is built from server-side.
const STATES: WorkplaceState[] = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const STATE_OPTIONS = STATES.map((value) => ({ label: value, value }));
const ACT_ELECTION_OPTIONS = [
  { label: 'ACT', value: 'ACT' },
  { label: 'Comcare', value: 'Comcare' },
];

interface WorkplaceDraft {
  workplaceName: string;
  addressLine1: string;
  addressLine2: string;
  suburb: string;
  state: WorkplaceState | '';
  postcode: string;
  workersCompJurisdiction: string;
}

const blankDraft = (): WorkplaceDraft => ({
  workplaceName: '', addressLine1: '', addressLine2: '', suburb: '', state: '', postcode: '', workersCompJurisdiction: '',
});

const draftFrom = (item: WorkplaceDetailsDto): WorkplaceDraft => ({
  workplaceName: item.workplaceName,
  addressLine1: item.address.addressLine1,
  addressLine2: item.address.addressLine2 ?? '',
  suburb: item.address.suburb,
  state: item.address.state as WorkplaceState,
  postcode: item.address.postcode,
  workersCompJurisdiction: item.isWorkersCompJurisdictionOperatorSet ? item.workersCompJurisdiction : '',
});

const validate = (draft: WorkplaceDraft): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!draft.workplaceName.trim()) errors.workplaceName = 'Workplace name is required.';
  if (!draft.addressLine1.trim()) errors.addressLine1 = 'Address line 1 is required.';
  if (!draft.suburb.trim()) errors.suburb = 'Suburb is required.';
  if (!draft.postcode.trim()) errors.postcode = 'Postcode is required.';
  if (!draft.state) errors.state = 'State is required.';
  if (draft.state === 'ACT' && !draft.workersCompJurisdiction)
    errors.workersCompJurisdiction = 'Choose ACT or Comcare for an ACT Workplace.';
  return errors;
};

const toRequest = (draft: WorkplaceDraft, idempotencyKey: string): SaveWorkplaceRequestDto => ({
  workplaceName: draft.workplaceName.trim(),
  addressLine1: draft.addressLine1.trim(),
  addressLine2: draft.addressLine2.trim() || null,
  suburb: draft.suburb.trim(),
  state: draft.state as WorkplaceState,
  postcode: draft.postcode.trim(),
  workersCompJurisdiction: draft.state === 'ACT' ? draft.workersCompJurisdiction : null,
  idempotencyKey,
});

const message = (reason: unknown, fallback: string) => (reason instanceof Error ? reason.message : fallback);

// AC-018: the coverage tag distinguishes a confirmed gap from data that
// simply hasn't been verified yet (OQ-1) — "unverified" must never read as
// "clean", and a confirmed gap must never read as merely "unverified".
const coverageTag = (item: WorkplaceDetailsDto) => {
  if (item.payrollTaxGap && item.workersCompGap) return <Tag label="Coverage gap" tone="caution" />;
  if (item.payrollTaxGap) return <Tag label="Payroll tax gap" tone="caution" />;
  if (item.workersCompGap) return <Tag label="Workers comp gap" tone="caution" />;
  if (!item.coverageVerified) return <Tag label="Not verified" tone="neutral" />;
  return <Tag label="Covered" tone="positive" />;
};

export const WorkplacesPanel = ({ businessRecordId }: Props) => {
  const [data, setData] = useState<WorkplaceListDetailsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  const [mode, setMode] = useState<'create' | 'edit' | null>(null);
  const [selected, setSelected] = useState<WorkplaceDetailsDto | null>(null);
  const [draft, setDraft] = useState<WorkplaceDraft>(blankDraft);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // C-10/T-22: one stable key per form session, minted the moment the modal
  // opens, reused across every resubmit of that same save. A new key is
  // minted only when a *new* add/edit is started (a new modal open).
  const saveIdempotencyKey = useRef('');

  const [closing, setClosing] = useState<WorkplaceDetailsDto | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [closeErrors, setCloseErrors] = useState<Record<string, string>>({});
  const [closeSaving, setCloseSaving] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [blockingReferences, setBlockingReferences] = useState<WorkplaceDetailsDto['blockingReferences']>(null);
  const closeIdempotencyKey = useRef('');

  const { addToast, removeToast, toasts } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await getWorkplaces(businessRecordId));
    } catch (reason) {
      setLoadError(message(reason, 'Workplaces could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [businessRecordId]);

  useEffect(() => { void load(); }, [load, refresh]);

  const openCreate = () => {
    setMode('create');
    setSelected(null);
    setDraft(blankDraft());
    setErrors({});
    setSaveError(null);
    saveIdempotencyKey.current = crypto.randomUUID();
  };

  const openEdit = (item: WorkplaceDetailsDto) => {
    setMode('edit');
    setSelected(item);
    setDraft(draftFrom(item));
    setErrors({});
    setSaveError(null);
    saveIdempotencyKey.current = crypto.randomUUID();
  };

  const closeModal = () => {
    if (saving) return;
    setMode(null);
    setSelected(null);
    setSaveError(null);
  };

  const save = async () => {
    const nextErrors = validate(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const request = toRequest(draft, saveIdempotencyKey.current);
      const result = mode === 'edit' && selected
        ? await updateWorkplace(businessRecordId, selected.workplaceId, request)
        : await createWorkplace(businessRecordId, request);
      setMode(null);
      setSelected(null);
      addToast(`Workplace saved: ${result.workplaceName}`, 'positive');
      result.advisories.forEach((advisory) => addToast(advisory.message, 'caution', { sticky: true }));
      setRefresh((value) => value + 1);
    } catch (reason) {
      setSaveError(message(reason, 'The Workplace could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const openClose = (item: WorkplaceDetailsDto) => {
    setClosing(item);
    setCloseReason('');
    setCloseErrors({});
    setCloseError(null);
    setBlockingReferences(null);
    closeIdempotencyKey.current = crypto.randomUUID();
  };

  const cancelClose = () => {
    if (closeSaving) return;
    setClosing(null);
    setCloseError(null);
    setBlockingReferences(null);
  };

  const confirmClose = async () => {
    if (!closing) return;
    if (!closeReason.trim()) {
      setCloseErrors({ reason: 'Reason is required.' });
      return;
    }
    setCloseSaving(true);
    setCloseError(null);
    try {
      await closeWorkplace(businessRecordId, closing.workplaceId, { reason: closeReason.trim(), idempotencyKey: closeIdempotencyKey.current });
      addToast(`Workplace closed: ${closing.workplaceName}`, 'positive');
      setClosing(null);
      setBlockingReferences(null);
      setRefresh((value) => value + 1);
    } catch (reason) {
      if (reason instanceof WorkplaceCloseBlockedError) {
        setBlockingReferences(reason.blockingReferences);
        setCloseError(reason.message);
      } else {
        setCloseError(message(reason, 'The Workplace could not be closed.'));
      }
    } finally {
      setCloseSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <div aria-busy="true" aria-label="Loading Workplaces" className="mgo-cluster mgo-justify-center mgo-border-neutral" style={{ minHeight: '15rem' }}>
        <div style={{ fontSize: 'var(--mgo-size-80)' }}><Spinner /></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <Callout tone="critical" title="Workplaces could not be loaded" action={<Button label="Retry" size="small" variant="secondary" onClick={() => setRefresh((value) => value + 1)} />}>
        {loadError}
      </Callout>
    );
  }

  const workplaces = data?.workplaces ?? [];
  const canEdit = data?.meta.actions.canEdit ?? false;
  const canAdd = (data?.meta.actions.canAdd ?? false);
  const canClose = data?.meta.actions.canClose ?? false;
  const readOnly = data?.meta.businessRecordInactive ?? false;

  const columns = [
    {
      field: 'workplaceName',
      header: 'Workplace',
      body: (row: WorkplaceDetailsDto) => (
        <div className="mgo-stack mgo-gap-xs">
          <span>{row.workplaceName}</span>
          <span className="mgo-text-xs mgo-color-muted">{row.address.addressLine1}, {row.address.suburb} {row.address.postcode}</span>
        </div>
      ),
    },
    { field: 'state', header: 'State', body: (row: WorkplaceDetailsDto) => <Tag label={row.address.state} tone="grey" /> },
    { field: 'payrollTaxNexusState', header: 'Payroll tax nexus', body: (row: WorkplaceDetailsDto) => <span>{row.payrollTaxNexusState}</span> },
    {
      field: 'workersCompJurisdiction',
      header: 'Workers comp jurisdiction',
      body: (row: WorkplaceDetailsDto) => <span>{row.workersCompJurisdiction}</span>,
    },
    {
      field: 'regionalRateEligible',
      header: 'Regional',
      body: (row: WorkplaceDetailsDto) => (row.regionalRateEligible === true ? <Tag label="Regional rate" tone="blue" /> : null),
    },
    { field: 'coverage', header: 'Coverage', body: coverageTag },
    {
      field: 'actions',
      header: 'Actions',
      body: (row: WorkplaceDetailsDto) => row.status === 'closed' ? null : (
        <div className="mgo-cluster mgo-gap-s">
          <Button label="Edit" variant="tertiary" size="small" disabled={!canEdit || readOnly} onClick={() => openEdit(row)} />
          <Button label="Close" variant="tertiary" size="small" disabled={!canClose || readOnly} onClick={() => openClose(row)} />
        </div>
      ),
    },
  ];

  return (
    <div className="mgo-stack mgo-gap-l">
      {data && (data.coverage.anyGap || data.coverage.anyUnverified) && (
        <Callout tone={data.coverage.anyGap ? 'caution' : 'info'} title={data.coverage.anyGap ? 'Coverage gaps detected' : 'Coverage cannot be confirmed'}>
          {data.coverage.anyGap
            ? `${data.coverage.payrollTaxGapCount} Workplace(s) have a Payroll tax gap and ${data.coverage.workersCompGapCount} have a Workers compensation gap. Configure the missing registrations on the Payroll tax and Workers compensation tabs.`
            : 'Payroll tax and Workers compensation configuration is not available yet, so coverage cannot be confirmed for one or more Workplaces.'}
        </Callout>
      )}

      {readOnly && (
        <Callout tone="neutral" title="Workplaces are read-only">
          This Business Record is inactive, so Workplaces can't be added, edited, or closed.
        </Callout>
      )}

      <Card
        header={
          <div className="mgo-split mgo-align-center">
            <span className="mgo-text-m mgo-weight-strong">Workplaces</span>
            <Button label="Add workplace" icon="add" variant="primary" disabled={!canAdd || readOnly} onClick={openCreate} />
          </div>
        }
      >
        {workplaces.length === 0 ? (
          <EmptyState
            variant="no-data"
            title="No Workplaces yet"
            description="Add the physical addresses this Business Record employs people at."
            actions={[<Button key="add" label="Add workplace" icon="add" disabled={!canAdd || readOnly} onClick={openCreate} />]}
          />
        ) : (
          <Table value={workplaces} columns={columns} scrollable testId="workplaces-table" />
        )}
      </Card>

      {mode && (
        <Modal
          visible
          header={mode === 'edit' ? `Edit ${selected?.workplaceName}` : 'Add workplace'}
          onHide={closeModal}
          footer={(
            <>
              <Button label="Cancel" variant="secondary" disabled={saving} onClick={closeModal} />
              <Button label={mode === 'edit' ? 'Save changes' : 'Add workplace'} variant="primary" loading={saving} onClick={() => void save()} />
            </>
          )}
        >
          <div className="mgo-stack mgo-gap-l">
            {saveError && <Callout tone="critical">{saveError}</Callout>}
            <TextField label="Workplace name" required value={draft.workplaceName} status={errors.workplaceName ? 'critical' : undefined} statusText={errors.workplaceName} onChange={(event) => setDraft({ ...draft, workplaceName: event.target.value })} />
            <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
              <TextField label="Address line 1" required value={draft.addressLine1} status={errors.addressLine1 ? 'critical' : undefined} statusText={errors.addressLine1} onChange={(event) => setDraft({ ...draft, addressLine1: event.target.value })} />
              <TextField label="Address line 2" value={draft.addressLine2} onChange={(event) => setDraft({ ...draft, addressLine2: event.target.value })} />
            </div>
            <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
              <TextField label="Suburb" required value={draft.suburb} status={errors.suburb ? 'critical' : undefined} statusText={errors.suburb} onChange={(event) => setDraft({ ...draft, suburb: event.target.value })} />
              <TextField label="Postcode" required value={draft.postcode} status={errors.postcode ? 'critical' : undefined} statusText={errors.postcode} onChange={(event) => setDraft({ ...draft, postcode: event.target.value })} />
            </div>
            <SelectField
              label="State"
              required
              searchable={false}
              options={STATE_OPTIONS}
              value={draft.state || null}
              status={errors.state ? 'critical' : ''}
              statusText={errors.state}
              onChange={(value) => setDraft({ ...draft, state: (typeof value === 'string' ? value : '') as WorkplaceState | '', workersCompJurisdiction: '' })}
            />
            {draft.state === 'ACT' && (
              <SelectField
                label="Workers compensation jurisdiction"
                required
                searchable={false}
                options={ACT_ELECTION_OPTIONS}
                value={draft.workersCompJurisdiction || null}
                status={errors.workersCompJurisdiction ? 'critical' : ''}
                statusText={errors.workersCompJurisdiction}
                helpText="ACT Workplaces must elect between the ACT scheme and Comcare."
                onChange={(value) => setDraft({ ...draft, workersCompJurisdiction: typeof value === 'string' ? value : '' })}
              />
            )}
          </div>
        </Modal>
      )}

      {closing && (
        <AlertDialog
          visible
          header={`Close ${closing.workplaceName}?`}
          confirmLabel="Close workplace"
          confirmLoading={closeSaving}
          onConfirm={() => void confirmClose()}
          onCancel={cancelClose}
        >
          <div className="mgo-stack mgo-gap-l">
            <p>Closing this Workplace ends its current version. Historical data remains readable.</p>
            <TextField
              label="Reason"
              required
              value={closeReason}
              status={closeErrors.reason ? 'critical' : undefined}
              statusText={closeErrors.reason}
              onChange={(event) => setCloseReason(event.target.value)}
            />
            {closeError && <Callout tone="critical">{closeError}</Callout>}
            {blockingReferences && blockingReferences.length > 0 && (
              <Callout tone="critical" title="This Workplace cannot be closed">
                <ul>
                  {blockingReferences.map((reference) => (
                    <li key={reference.referenceId}>{reference.kind}: {reference.label}</li>
                  ))}
                </ul>
              </Callout>
            )}
          </div>
        </AlertDialog>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};
